import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Truck,
  Package,
  AlertTriangle,
  XCircle,
  ShoppingCart,
  ArrowLeft,
  Calculator,
  FileSpreadsheet,
  CheckCircle,
  Printer,
  Eye,
  Download,
  Plus,
  Minus,
  Search,
  ChevronsUpDown,
  Check,
  Clock,
  Info,
  X,
  AlertCircle,
  Users,
  Star,
  RefreshCw,
  Pencil,
  Save,
  Filter,
  FolderOpen,
  Upload,
  Trash2,
  Loader2,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Warehouse,
  Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { SupplierFiscalConfig, StatusBadge as FiscalStatusBadge } from '@/components/SupplierFiscalConfig';

const SupplierOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Continuing draft state
  const [continuingDraft, setContinuingDraft] = useState(null); // po_number being continued
  const [draftData, setDraftData] = useState(null); // existing PO data
  
  // Order confirmation state - now supports multiple orders
  const [createdOrders, setCreatedOrders] = useState(null);  // Array of orders by supplier
  const [orderDetailsMap, setOrderDetailsMap] = useState({});  // Map of po_number -> details
  
  // Export options
  const [exportArmazem, setExportArmazem] = useState('001');
  const [exportFormato, setExportFormato] = useState('xlsx');
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // Alternative suppliers state
  const [productSuppliers, setProductSuppliers] = useState({});  // Map of sku -> suppliers list
  const [loadingSuppliers, setLoadingSuppliers] = useState({});  // Loading state per product
  const [supplierOverrides, setSupplierOverrides] = useState({});  // Manual supplier selections
  const [showSupplierModal, setShowSupplierModal] = useState(null);  // Product to show modal for
  const [selectedExportOrder, setSelectedExportOrder] = useState(null);
  
  // Price editing state
  const [editingPrice, setEditingPrice] = useState(null);  // supplier id being edited
  const [editPriceValue, setEditPriceValue] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Manual product add
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [manualProducts, setManualProducts] = useState([]);  // Products added manually
  const [searchingProducts, setSearchingProducts] = useState(false);  // Loading state for product search
  const [searchTotal, setSearchTotal] = useState(0);  // Total results from server
  
  // Product detail panel (slide-over) state
  const [productDetailPanel, setProductDetailPanel] = useState({ open: false, product: null, loading: false, data: null });
  
  // Pending orders protection
  const [pendingOrders, setPendingOrders] = useState([]);  // Encomendas pendentes do fornecedor
  const [pendingByProduct, setPendingByProduct] = useState({});  // Qtd pendente por produto
  
  // Cart items for supplier
  const [cartItemsForSupplier, setCartItemsForSupplier] = useState([]);
  
  // Department filter
  const [departmentFilter, setDepartmentFilter] = useState('all');  // Itens do carrinho para este fornecedor
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierSort, setSupplierSort] = useState('name');  // name | needing | critical
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewIds, setReviewIds] = useState([]);  // Linhas fixadas ao entrar na revisão
  const [sendToSage, setSendToSage] = useState(true);
  // Toggle: only show suppliers with products needing order (default ON)
  const [showOnlyPending, setShowOnlyPending] = useState(true);
  
  // Advanced filters for supplier products view
  const [riskFilters, setRiskFilters] = useState([]); // Multi-select: ['critical', 'high', 'medium', 'low']
  const [showZeroStockOnly, setShowZeroStockOnly] = useState(false); // Toggle for stock = 0
  
  // Product search within supplier view (NEW)
  const [productSearchQuery, setProductSearchQuery] = useState('');
  
  // Supplier fiscal configuration (Phase 1)
  const [fiscalConfigModal, setFiscalConfigModal] = useState({ open: false, supplierCode: null, supplierName: null });
  const [supplierFiscalStatus, setSupplierFiscalStatus] = useState({}); // Map: supplierCode -> fiscal status

  // Get auth token for fiscal config component
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  // Fetch fiscal status for current supplier when entering review
  const fetchFiscalStatus = useCallback(async (supplierCode) => {
    if (!supplierCode) return null;
    try {
      const res = await axios.get(`${API}/suppliers/${supplierCode}/fiscal`);
      setSupplierFiscalStatus(prev => ({ ...prev, [supplierCode]: res.data }));
      return res.data;
    } catch (err) {
      console.error('Error fetching fiscal status:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle continue draft from URL params
  useEffect(() => {
    const continuePoNumber = searchParams.get('continue');
    const supplierName = searchParams.get('supplier');
    
    if (continuePoNumber && supplierName && !continuingDraft) {
      // Fetch PO and supplier info directly, don't wait for suppliers list
      handleContinueDraftDirect(continuePoNumber, supplierName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleContinueDraftDirect = async (poNumber, supplierName) => {
    try {
      setContinuingDraft(poNumber);
      setSearchParams({}); // Clear URL params immediately
      
      // Fetch PO details first
      const poResponse = await axios.get(`${API}/purchase-orders/by-number/${poNumber}`);
      const poData = poResponse.data;
      
      // Construct supplier object from PO data
      const supplierObj = {
        name: poData.supplier || supplierName,
        sage_code: poData.items?.[0]?.sage_supplier_code || null
      };
      
      // Set selected supplier
      setSelectedSupplier(supplierObj);
      setLoading(true);
      
      // Fetch products for this supplier
      const productsResponse = await axios.get(`${API}/suppliers/${encodeURIComponent(supplierObj.name)}/products`);
      setSupplierProducts(productsResponse.data);
      
      // Pre-select items from the draft PO
      const newSelectedItems = {};
      const newQuantities = {};
      const manualItemsToAdd = [];
      
      for (const item of poData.items) {
        newSelectedItems[item.product_id] = true;
        newQuantities[item.product_id] = item.quantity;
        
        // Check if product is in supplier's list
        const inSupplierList = productsResponse.data.products?.some(p => p.product_id === item.product_id);
        if (!inSupplierList) {
          // Add as manual product
          manualItemsToAdd.push({
            product_id: item.product_id,
            sku: item.sku,
            product_name: item.product_name,
            unit_price: item.unit_price,
            current_stock: 0,
            supplier: item.supplier,
            isManual: true,
            fromDraft: true
          });
        }
      }
      
      setSelectedItems(newSelectedItems);
      setQuantities(newQuantities);
      setManualProducts(manualItemsToAdd);
      setDraftData(poData);
      
      toast.success(`Rascunho ${poNumber} carregado com ${poData.total_items} artigos`);
    } catch (error) {
      toast.error('Erro ao carregar rascunho: ' + (error.response?.data?.detail || error.message));
      console.error(error);
      // Return to suppliers list
      setSelectedSupplier(null);
    } finally {
      setLoading(false);
      setContinuingDraft(null);
    }
  };

  const handleContinueDraft = async (supplier, poNumber) => {
    // Use the direct method with supplier name from object
    await handleContinueDraftDirect(poNumber, supplier.name);
  };
  
  // Server-side product search with debounce (same as ShoppingCart)
  useEffect(() => {
    let cancelled = false;
    const query = searchQuery.trim();
    
    // Don't fire requests for very short queries (server has 14k+ products)
    if (query.length < 2) {
      setAllProducts([]);
      setSearchTotal(0);
      return undefined;
    }
    
    setSearchingProducts(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${API}/products?search=${encodeURIComponent(query)}&page_size=100`
        );
        if (!cancelled) {
          setAllProducts(response.data.items || []);
          setSearchTotal(response.data.total || 0);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Erro ao pesquisar produtos', error);
          setAllProducts([]);
          setSearchTotal(0);
        }
      } finally {
        if (!cancelled) setSearchingProducts(false);
      }
    }, 250);  // 250ms debounce
    
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery]);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/list?active_only=true`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingOrders = async (supplierName) => {
    try {
      const response = await axios.get(`${API}/suppliers/${encodeURIComponent(supplierName)}/pending-orders`);
      setPendingOrders(response.data.orders || []);
      setPendingByProduct(response.data.pending_by_product || {});
      return response.data;
    } catch (error) {
      console.error('Failed to load pending orders');
      setPendingOrders([]);
      setPendingByProduct({});
      return { orders: [], pending_by_product: {} };
    }
  };

  const fetchSupplierProducts = async (supplierName) => {
    setLoading(true);
    try {
      // Fetch products, pending orders, and cart items in parallel
      const [productsResponse, pendingData, cartResponse] = await Promise.all([
        axios.get(`${API}/suppliers/${encodeURIComponent(supplierName)}/products`),
        fetchPendingOrders(supplierName),
        axios.get(`${API}/shopping-cart/by-supplier/${encodeURIComponent(supplierName)}`)
      ]);
      
      setSupplierProducts(productsResponse.data);
      
      // Store cart items
      const cartItems = cartResponse.data.items || [];
      setCartItemsForSupplier(cartItems);
      
      // Create a map of cart items by product_id
      const cartByProduct = {};
      cartItems.forEach(item => {
        cartByProduct[item.product_id] = item;
      });

      // Convergência com o carrinho: artigos no carrinho deste fornecedor
      // entram PRÉ-SELECIONADOS nesta encomenda (o admin pode desmarcar).
      // Itens do carrinho que não estão na lista do fornecedor entram como linhas extra.
      const listIds = new Set(productsResponse.data.products.map(p => p.product_id));
      const cartOnlyRows = cartItems.filter(ci => !listIds.has(ci.product_id)).map(ci => {
        const full = allProducts.find(p => p.id === ci.product_id);
        return {
          product_id: ci.product_id,
          sku: ci.sku,
          product_name: ci.product_name,
          category: full?.category || null,
          current_stock: full?.current_stock ?? 0,
          reorder_point: full?.reorder_point ?? 0,
          optimal_stock: full?.optimal_stock || 0,
          moq: full?.moq || 1,
          lead_time_days: full?.lead_time_days || 7,
          unit_price: ci.unit_price || full?.unit_price || 0,
          average_daily_sales: 0,
          days_until_stockout: null,
          risk_level: 'low',
          suggested_quantity: ci.quantity,
          total_value: (ci.unit_price || 0) * ci.quantity,
          needs_order: false,
          isManual: true,
          fromCart: true,
        };
      });

      // Initialize quantities with suggested values, adjusting for pending AND cart
      const initialQuantities = {};
      const initialSelected = {};
      productsResponse.data.products.forEach(p => {
        // Descontar quantidade pendente E do carrinho da sugestão
        const pendingQty = pendingData.pending_by_product?.[p.product_id]?.total_pending || 0;
        const cartQty = cartByProduct[p.product_id]?.quantity || 0;
        const adjustedSuggestion = Math.max(0, p.suggested_quantity - pendingQty - cartQty);

        // Carrinho converge: qty inicial = qty do carrinho + necessidade adicional
        initialQuantities[p.product_id] = cartQty + adjustedSuggestion;
        initialSelected[p.product_id] = cartQty > 0 || (p.needs_order && adjustedSuggestion > 0);
      });
      cartOnlyRows.forEach(r => {
        initialQuantities[r.product_id] = cartByProduct[r.product_id]?.quantity || 1;
        initialSelected[r.product_id] = true;
      });
      setQuantities(initialQuantities);
      setSelectedItems(initialSelected);
      setManualProducts(cartOnlyRows);
    } catch (error) {
      toast.error('Erro ao carregar produtos do fornecedor');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter products for manual add (any product not already in the list)
  // Server already filtered by searchQuery, we just exclude products already in the list
  const filteredProductsForAdd = useMemo(() => {
    if (!supplierProducts) return [];
    
    const existingIds = new Set([
      ...supplierProducts.products.map(p => p.product_id),
      ...manualProducts.map(p => p.product_id)
    ]);
    
    // Filter products that are not already in the list (from any supplier)
    // Server already filtered by search query, so we just exclude existing products
    return allProducts.filter(p => !existingIds.has(p.id));
  }, [allProducts, supplierProducts, manualProducts]);
  
  // Check if there are any products available to add (only when search has results)
  const hasAvailableProducts = useMemo(() => {
    return filteredProductsForAdd.length > 0;
  }, [filteredProductsForAdd]);
  
  const handleAddManualProduct = (product) => {
    const newManualProduct = {
      product_id: product.id,
      sku: product.sku,
      product_name: product.name,
      category: product.category,
      current_stock: product.current_stock,
      reorder_point: product.reorder_point,
      optimal_stock: product.optimal_stock || 100,
      moq: product.moq || 1,
      lead_time_days: product.lead_time_days || 7,
      unit_price: product.unit_price,
      average_daily_sales: 0,
      days_until_stockout: null,
      risk_level: 'low',
      suggested_quantity: product.moq || 1,
      total_value: 0,
      needs_order: false,
      isManual: true
    };
    
    setManualProducts([...manualProducts, newManualProduct]);
    setQuantities(prev => ({
      ...prev,
      [product.id]: product.moq || 1
    }));
    setSelectedItems(prev => ({
      ...prev,
      [product.id]: true
    }));
    
    setSearchQuery('');
    setSearchOpen(false);
    toast.success(`Produto ${product.sku} adicionado`);
  };
  
  const handleRemoveManualProduct = (productId) => {
    setManualProducts(manualProducts.filter(p => p.product_id !== productId));
    const newQuantities = { ...quantities };
    delete newQuantities[productId];
    setQuantities(newQuantities);
    const newSelected = { ...selectedItems };
    delete newSelected[productId];
    setSelectedItems(newSelected);
  };
  
  // Fetch alternative suppliers for a product
  const fetchProductSuppliers = async (sku) => {
    if (productSuppliers[sku]) return; // Already fetched
    
    setLoadingSuppliers(prev => ({ ...prev, [sku]: true }));
    try {
      const response = await axios.get(`${API}/products/${sku}/suppliers`);
      setProductSuppliers(prev => ({
        ...prev,
        [sku]: response.data.suppliers || []
      }));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoadingSuppliers(prev => ({ ...prev, [sku]: false }));
    }
  };
  
  // Handle supplier change for a product
  const handleSupplierOverride = (sku, supplier) => {
    setSupplierOverrides(prev => ({
      ...prev,
      [sku]: supplier
    }));
    setShowSupplierModal(null);
    toast.success(`Fornecedor alterado para ${supplier.supplier_name}`);
  };
  
  // Handle price update for a supplier
  const handleSavePrice = async (supplier) => {
    const price = parseFloat(editPriceValue);
    if (isNaN(price) || price < 0) {
      toast.error('Preço inválido');
      return;
    }
    
    setSavingPrice(true);
    try {
      await axios.patch(`${API}/product-suppliers/${supplier.id}`, {
        last_purchase_price: price,
        price_source: 'manual'
      });
      
      // Update local state
      const sku = showSupplierModal?.sku;
      if (sku && productSuppliers[sku]) {
        setProductSuppliers(prev => ({
          ...prev,
          [sku]: prev[sku].map(s => 
            s.id === supplier.id 
              ? { ...s, last_purchase_price: price, last_purchase_date: new Date().toISOString(), price_source: 'manual' }
              : s
          )
        }));
      }
      
      setEditingPrice(null);
      setEditPriceValue('');
      toast.success(`Preço actualizado: €${price.toFixed(2)}`);
    } catch (error) {
      console.error('Error saving price:', error);
      toast.error('Erro ao guardar preço');
    } finally {
      setSavingPrice(false);
    }
  };
  
  // Get the effective supplier for a product (override or default)
  const getEffectiveSupplier = (product) => {
    const override = supplierOverrides[product.sku];
    if (override) {
      return {
        name: override.supplier_name,
        code: override.supplier_code,
        isOverride: true
      };
    }
    return {
      name: supplierProducts?.supplier || product.supplier || 'N/A',
      code: null,
      isOverride: false
    };
  };
  
  // Combined products list (supplier + manual)
  const allSupplierProducts = useMemo(() => {
    if (!supplierProducts) return [];
    return [...supplierProducts.products, ...manualProducts];
  }, [supplierProducts, manualProducts]);

  // Filtered products based on advanced filters (risk multi-select + zero stock + search)
  const filteredSupplierProducts = useMemo(() => {
    let filtered = allSupplierProducts;
    
    // Apply product search filter (SKU, name, supplier reference)
    if (productSearchQuery.trim()) {
      const q = productSearchQuery.trim().toLowerCase();
      filtered = filtered.filter(p => 
        (p.sku || '').toLowerCase().includes(q) ||
        (p.product_name || '').toLowerCase().includes(q) ||
        (p.supplier_ref || '').toLowerCase().includes(q)
      );
    }
    
    // Apply risk level multi-filter (if any selected)
    if (riskFilters.length > 0) {
      filtered = filtered.filter(p => riskFilters.includes(p.risk_level));
    }
    
    // Apply zero stock filter
    if (showZeroStockOnly) {
      filtered = filtered.filter(p => (p.current_stock ?? 0) === 0);
    }
    
    return filtered;
  }, [allSupplierProducts, riskFilters, showZeroStockOnly, productSearchQuery]);

  // Check if filters are active
  const hasActiveFilters = riskFilters.length > 0 || showZeroStockOnly || productSearchQuery.trim().length > 0;

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setRiskFilters([]);
    setShowZeroStockOnly(false);
    setProductSearchQuery('');
  };

  // Toggle a risk level in the multi-select
  const toggleRiskFilter = (riskLevel) => {
    setRiskFilters(prev => 
      prev.includes(riskLevel) 
        ? prev.filter(r => r !== riskLevel)
        : [...prev, riskLevel]
    );
  };

  const cartMap = useMemo(() => {
    const m = {};
    cartItemsForSupplier.forEach(ci => { m[ci.product_id] = ci; });
    return m;
  }, [cartItemsForSupplier]);

  // Deep-link do chip "🚚 a caminho" para a encomenda pendente respetiva
  const getPendingPoLink = (productId) => {
    const orders = pendingByProduct[productId]?.orders || [];
    if (orders.length === 0) return null;
    return `/purchase-orders?po=${encodeURIComponent(orders[0].po_number)}`;
  };

  const getPendingPoTitle = (productId, qty) => {
    const orders = pendingByProduct[productId]?.orders || [];
    const pos = [...new Set(orders.map(o => o.po_number))];
    if (pos.length === 0) return `${qty} unidade(s) encomendada(s) por receber`;
    return `${qty} unidade(s) por receber em: ${pos.join(', ')} — clique para abrir`;
  };


  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    fetchSupplierProducts(supplier.supplier);
    // Fetch fiscal status for this supplier
    if (supplier.sage_code) {
      fetchFiscalStatus(supplier.sage_code);
    }
  };

  const handleBackToSuppliers = () => {
    setSelectedSupplier(null);
    setSupplierProducts(null);
    setSelectedItems({});
    setQuantities({});
    setCreatedOrders(null);
    setOrderDetailsMap({});
    setManualProducts([]);
    setPendingOrders([]);
    setPendingByProduct({});
    // Clear advanced filters when leaving supplier view
    setRiskFilters([]);
    setShowZeroStockOnly(false);
    setProductSearchQuery('');
    // Clear draft state
    setDraftData(null);
    setContinuingDraft(null);
  };

  const handleBackToProducts = () => {
    setCreatedOrders(null);
    setOrderDetailsMap({});
  };

  const handleToggleItem = (productId) => {
    setSelectedItems(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(0, parseInt(value) || 0)
    }));
  };

  const handleSelectAll = () => {
    const newSelected = {};
    // Select only from filtered products (respects advanced filters)
    filteredSupplierProducts.forEach(p => {
      newSelected[p.product_id] = true;
    });
    setSelectedItems(newSelected);
  };

  const handleSelectNeedsOrder = () => {
    const newSelected = {};
    // Select only from filtered products that need ordering
    filteredSupplierProducts.forEach(p => {
      if (p.needs_order) {
        newSelected[p.product_id] = true;
      }
    });
    setSelectedItems(newSelected);
  };

  const handleDeselectAll = () => {
    setSelectedItems({});
  };

  // Select products by risk level
  const handleSelectByRisk = (riskLevel) => {
    const newSelected = { ...selectedItems };
    supplierProducts.products.forEach(p => {
      if (p.risk_level === riskLevel) {
        newSelected[p.product_id] = true;
      }
    });
    setSelectedItems(newSelected);
  };

  // Select only products of a specific risk level (exclusive)
  const handleSelectOnlyRisk = (riskLevel) => {
    const newSelected = {};
    supplierProducts.products.forEach(p => {
      if (p.risk_level === riskLevel) {
        newSelected[p.product_id] = true;
      }
    });
    setSelectedItems(newSelected);
  };

  // Open product detail panel (slide-over)
  const openProductDetailPanel = async (product) => {
    setProductDetailPanel({ open: true, product, loading: true, data: null });
    try {
      const [productRes, fichaRes] = await Promise.all([
        axios.get(`${API}/products/${product.product_id}`),
        axios.get(`${API}/procurement/ficha/${product.sku}`).catch(() => ({ data: null }))
      ]);
      setProductDetailPanel(prev => ({
        ...prev,
        loading: false,
        data: {
          ...productRes.data,
          ficha: fichaRes.data
        }
      }));
    } catch (error) {
      console.error('Error loading product details:', error);
      setProductDetailPanel(prev => ({ ...prev, loading: false }));
      toast.error('Erro ao carregar detalhes do produto');
    }
  };

  const closeProductDetailPanel = () => {
    setProductDetailPanel({ open: false, product: null, loading: false, data: null });
  };

  const calculateTotals = () => {
    let totalItems = 0;
    let totalValue = 0;
    
    allSupplierProducts.forEach(p => {
      if (selectedItems[p.product_id] && quantities[p.product_id] > 0) {
        totalItems++;
        totalValue += quantities[p.product_id] * p.unit_price;
      }
    });
    
    return { totalItems, totalValue };
  };

  const handleSubmitOrder = async () => {
    const ids = allSupplierProducts
      .filter(p => selectedItems[p.product_id] && quantities[p.product_id] > 0)
      .map(p => p.product_id);
    if (ids.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    // Fetch fiscal status for current supplier if sendToSage is enabled
    if (sendToSage && selectedSupplier?.sage_code) {
      await fetchFiscalStatus(selectedSupplier.sage_code);
    }
    
    // Abrir página de revisão — a PO só é criada após confirmação
    setReviewIds(ids);
    setShowReviewModal(true);
    window.scrollTo(0, 0);
  };

  const confirmCreateOrder = async () => {
    const items = [];
    allSupplierProducts.forEach(p => {
      if (selectedItems[p.product_id] && quantities[p.product_id] > 0) {
        items.push({
          product_id: p.product_id,
          quantity: quantities[p.product_id]
        });
      }
    });
    if (items.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    await executeCreateOrder(items);
  };

  const executeCreateOrder = async (items) => {
    setSubmitting(true);
    
    try {
      let orders;
      let total_orders;
      
      // If continuing a draft, add items to existing PO
      if (draftData && draftData.po_number) {
        // Identify new items (not already in draft)
        const existingSkus = new Set(draftData.items.map(i => i.sku));
        const existingProductIds = new Set(draftData.items.map(i => i.product_id));
        
        // Items to add or update
        for (const item of items) {
          if (!existingProductIds.has(item.product_id)) {
            // Add new line to existing PO
            await axios.post(`${API}/purchase-orders/${draftData.po_number}/items`, {
              product_id: item.product_id,
              quantity: item.quantity
            });
          } else {
            // Update quantity if changed
            const existingItem = draftData.items.find(i => i.product_id === item.product_id);
            if (existingItem && existingItem.quantity !== item.quantity) {
              await axios.patch(`${API}/purchase-orders/items/${existingItem.id}/quantity?quantity=${item.quantity}`);
            }
          }
        }
        
        // Fetch updated PO
        const updatedPo = await axios.get(`${API}/purchase-orders/by-number/${draftData.po_number}`);
        orders = [{
          po_number: draftData.po_number,
          supplier: supplierProducts.supplier,
          total_items: updatedPo.data.total_items,
          total_amount: updatedPo.data.total_value
        }];
        total_orders = 1;
        toast.success(`Rascunho ${draftData.po_number} atualizado com sucesso!`);
        
        // Clear draft state
        setDraftData(null);
      } else {
        // Create new PO(s) with force_supplier=true and optionally send to SAGE
        const response = await axios.post(`${API}/purchase-orders/bulk?force_supplier=true&send_to_sage=${sendToSage}`, {
          supplier: supplierProducts.supplier,
          items: items
        });
        
        orders = response.data.orders;
        total_orders = response.data.total_orders;
        
        // Check for SAGE integration errors
        const sageSummary = response.data.sage_integration_summary;
        const sageErrors = orders.filter(o => o.sage_integration_error).map(o => o.sage_integration_error);
        
        if (sendToSage && sageErrors.length > 0) {
          // SAGE integration failed
          toast.error(`Encomenda criada mas NÃO integrada no SAGE: ${sageErrors[0]}`, {
            duration: 8000,
          });
        } else if (sendToSage && sageSummary?.successful > 0) {
          // SAGE integration successful
          if (total_orders === 1) {
            toast.success(`Encomenda ${orders[0].po_number} criada e integrada no SAGE!`);
          } else {
            toast.success(`${total_orders} encomendas criadas e integradas no SAGE!`);
          }
        } else {
          // No SAGE integration requested or no supplier code
          if (total_orders === 1) {
            toast.success(`Encomenda ${orders[0].po_number} criada com sucesso!`);
          } else {
            toast.success(`${total_orders} encomendas criadas com sucesso! (uma por fornecedor)`);
          }
        }
      }
      
      // Remove cart items for this supplier that were included in the order
      if (cartItemsForSupplier.length > 0) {
        const cartProductIds = cartItemsForSupplier
          .filter(ci => items.some(i => i.product_id === ci.product_id))
          .map(ci => ci.product_id);
        
        if (cartProductIds.length > 0) {
          try {
            await axios.delete(
              `${API}/shopping-cart/by-supplier/${encodeURIComponent(supplierProducts.supplier)}`,
              { params: { product_ids: cartProductIds } }
            );
            toast.info(`${cartProductIds.length} produto(s) removido(s) do carrinho`);
          } catch (cartError) {
            console.error('Failed to remove cart items:', cartError);
          }
        }
      }
      
      // Fetch order details for each PO
      const detailsMap = {};
      for (const order of orders) {
        const orderDetailsResponse = await axios.get(`${API}/purchase-orders/by-number/${order.po_number}`);
        detailsMap[order.po_number] = orderDetailsResponse.data;
      }
      
      setOrderDetailsMap(detailsMap);
      // BUG FIX 2026-09-18: Wrap orders array in object with proper structure
      setCreatedOrders({
        orders: orders,
        total_orders: total_orders,
        total_items: orders.reduce((sum, o) => sum + (o.total_items || 0), 0),
        total_amount: orders.reduce((sum, o) => sum + (o.total_amount || 0), 0)
      });
      setShowReviewModal(false);

      // SAGE integration is now handled directly in the /purchase-orders/bulk endpoint
      // No need for separate SAGE call
      
    } catch (error) {
      console.error('Error creating order:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao criar encomenda';
      toast.error(errorMessage, { duration: 8000 });
      // Don't crash - stay on current page
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportExcel = async (poNumber) => {
    if (!poNumber) return;
    
    const orderDetail = orderDetailsMap[poNumber];
    if (!orderDetail) return;
    
    try {
      const response = await axios.get(
        `${API}/purchase-orders/${poNumber}/export-excel`,
        { 
          responseType: 'blob',
          params: {
            armazem: exportArmazem,
            formato: exportFormato
          }
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeSupplier = orderDetail?.supplier?.replace(/[^\w\s.-]/g, '').replace(/\s+/g, '_') || 'Fornecedor';
      link.setAttribute('download', `${poNumber}_${safeSupplier}_SAGE100C.${exportFormato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Ficheiro Excel exportado com sucesso!');
      setShowExportDialog(false);
      setSelectedExportOrder(null);
    } catch (error) {
      toast.error('Erro ao exportar ficheiro Excel');
    }
  };
  
  const handleExportAll = async () => {
    if (!createdOrders?.orders) return;
    
    for (const order of createdOrders.orders) {
      await handleExportExcel(order.po_number);
    }
  };

  // Send PO to SAGE via API
  const [sendingSage, setSendingSage] = useState({});
  const [sageSentOrders, setSageSentOrders] = useState({});

  const handleSendToSage = async (poNumber) => {
    setSendingSage(prev => ({ ...prev, [poNumber]: true }));
    try {
      const response = await axios.post(
        `${API}/sage/create-purchase-order?po_number=${encodeURIComponent(poNumber)}`
      );
      const ref = response.data.sage_reference || response.data.message;
      const sageDocNumber = response.data.sage_document_number;
      const sageDocSerie = response.data.sage_document_serie;
      
      setSageSentOrders(prev => ({
        ...prev,
        [poNumber]: {
          success: true,
          sage_reference: ref,
          sage_po_number: response.data.sage_po_number || ref,
          sage_document_number: sageDocNumber,
          sage_document_serie: sageDocSerie,
        }
      }));
      
      // Update createdOrders to reflect sage_synced status
      setCreatedOrders(prev => {
        if (!prev?.orders) return prev;
        return {
          ...prev,
          orders: prev.orders.map(o => 
            o.po_number === poNumber 
              ? { ...o, sage_synced: true, sage_po_number: response.data.sage_po_number || ref }
              : o
          )
        };
      });
      
      if (response.data.already_sent) {
        toast.info(`${poNumber} já estava no SAGE: ${ref}`);
      } else {
        toast.success(`${poNumber} enviada ao SAGE: ${ref}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || 'Erro ao enviar ao SAGE');
      setSageSentOrders(prev => ({
        ...prev,
        [poNumber]: { success: false, error: msg }
      }));
      toast.error(msg);
    } finally {
      setSendingSage(prev => ({ ...prev, [poNumber]: false }));
    }
  };

  const handleSendAllToSage = async () => {
    if (!createdOrders?.orders) return;
    for (const order of createdOrders.orders) {
      if (!sageSentOrders[order.po_number]?.success) {
        await handleSendToSage(order.po_number);
      }
    }
  };

  // -------- Line-level edits (Sprint 1: minimum viable) --------
  // Rule: "antes de SAGE = editável; depois de SAGE = imutável."
  // A PO is editable when none of its lines are sage-synced and SAGE wasn't
  // marked as entered. We treat received_quantity > 0 as a stronger lock too.
  const isOrderEditable = (order, details) => {
    if (!order) return false;
    if (order.sage_synced || order.entered_sage_at) return false;
    if (sageSentOrders[order.po_number]?.success) return false;
    const lines = details?.items || [];
    if (lines.length <= 1) return false;  // can't remove the last line
    const status = details?.status || order.status || 'pending';
    if (!['pending', 'approved'].includes(status)) return false;
    return true;
  };

  const isLineEditable = (item, order, details) => {
    if (!isOrderEditable(order, details)) return false;
    if (item?.sage_synced || item?.entered_sage_at) return false;
    if ((item?.received_quantity || 0) > 0) return false;
    return true;
  };

  const [removingLineId, setRemovingLineId] = useState(null);

  // -------- Supplier coherence analysis (Sessão 25) --------
  // Source of truth: sage_supplier_code. Text supplier is only display.
  // Returns:
  //   { sageCodes: Set<string>, textSuppliers: Set<string>,
  //     isMixed: bool (multiple sage codes),
  //     hasTextDivergence: bool (same code, different texts),
  //     primarySageCode: string | null,
  //     breakdown: { [code]: [skus] } }
  const analyzeSupplierCoherence = (details) => {
    const lines = details?.items || [];
    const sageCodes = new Set();
    const textSuppliers = new Set();
    const breakdown = {};
    for (const ln of lines) {
      const code = (ln.sage_supplier_code || '').trim();
      const text = (ln.supplier || '').trim();
      if (code) {
        sageCodes.add(code);
        if (!breakdown[code]) breakdown[code] = [];
        breakdown[code].push(ln.sku);
      }
      if (text) textSuppliers.add(text);
    }
    return {
      sageCodes,
      textSuppliers,
      isMixed: sageCodes.size > 1,
      hasTextDivergence: sageCodes.size === 1 && textSuppliers.size > 1,
      missingSageCode: sageCodes.size === 0,
      primarySageCode: sageCodes.size === 1 ? [...sageCodes][0] : null,
      breakdown,
    };
  };

  const handleRemoveLine = async (item, poNumber) => {
    if (!item?.id) return;
    const ok = window.confirm(
      `Remover linha "${item.sku} — ${item.product_name}" (qtd ${item.quantity}, total €${(item.total_amount || 0).toFixed(2)})?\n\n` +
      `Esta acção só está disponível enquanto a encomenda não estiver no SAGE.`
    );
    if (!ok) return;
    setRemovingLineId(item.id);
    try {
      const response = await axios.delete(`${API}/purchase-orders/items/${item.id}`);
      // Backend returns the refreshed PO summary — use it directly to avoid an extra fetch
      setOrderDetailsMap(prev => ({ ...prev, [poNumber]: response.data }));
      toast.success(`Linha "${item.sku}" removida. Totais actualizados.`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao remover linha';
      toast.error(msg);
    } finally {
      setRemovingLineId(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getRiskBadge = (riskLevel) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      needs_configuration: 'bg-slate-100 text-slate-500 border-slate-200',
      out_of_scope: 'bg-slate-50 text-slate-400 border-slate-200',
      zero_stock_alert: 'bg-red-100 text-red-700 border-red-200'
    };
    const labels = {
      critical: 'Crítico',
      high: 'Risco alto',
      medium: 'Atenção',
      low: 'Saudável',
      needs_configuration: 'Sem parâmetros',
      out_of_scope: 'Fora de gestão',
      zero_stock_alert: 'Sem Stock'
    };
    if (!labels[riskLevel]) return null;
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-sm border ${styles[riskLevel]}`}
        title={
          riskLevel === 'low' ? 'Risco de rutura baixo — stock saudável' :
          riskLevel === 'zero_stock_alert' ? 'Stock a zero — produto com procura mas sem parâmetros de reposição automática' :
          undefined
        }
      >
        {labels[riskLevel]}
      </span>
    );
  };

  // Group suppliers by department (must be before conditional returns - React hooks rule)
  const groupedSuppliers = useMemo(() => {
    // Apply department filter first
    let filtered = departmentFilter === 'all'
      ? suppliers
      : departmentFilter === 'no-group'
        ? suppliers.filter(s => !s.group)
        : suppliers.filter(s => s.group === departmentFilter);
    
    // Apply "only pending" filter (suppliers with products that need ordering)
    if (showOnlyPending) {
      filtered = filtered.filter(s => (s.products_needing_order || 0) > 0);
    }

    // Apply text search (supplier name)
    if (supplierSearch.trim()) {
      const q = supplierSearch.trim().toLowerCase();
      filtered = filtered.filter(s => (s.supplier || '').toLowerCase().includes(q));
    }

    const groups = {};
    const noGroup = [];
    
    filtered.forEach(s => {
      const group = s.group || null;
      if (group) {
        if (!groups[group]) groups[group] = [];
        groups[group].push(s);
      } else {
        noGroup.push(s);
      }
    });

    // Ordenação escolhida pelo utilizador (alfabética por defeito)
    const sortFns = {
      name: (a, b) => (a.supplier || '').localeCompare(b.supplier || '', 'pt'),
      needing: (a, b) => (b.products_needing_order || 0) - (a.products_needing_order || 0),
      critical: (a, b) => (b.critical_items || 0) - (a.critical_items || 0),
    };
    const sortFn = sortFns[supplierSort] || sortFns.name;
    Object.values(groups).forEach(list => list.sort(sortFn));
    noGroup.sort(sortFn);
    
    const sortedGroupNames = Object.keys(groups).sort();
    const result = sortedGroupNames.map(name => ({ name, suppliers: groups[name] }));
    if (noGroup.length > 0) {
      result.push({ name: null, suppliers: noGroup });
    }
    return result;
  }, [suppliers, departmentFilter, showOnlyPending, supplierSearch, supplierSort]);

  // Extract unique department names for the filter
  const departmentNames = useMemo(() => {
    const names = new Set();
    suppliers.forEach(s => { if (s.group) names.add(s.group); });
    return Array.from(names).sort();
  }, [suppliers]);

  const renderSupplierCard = (supplier) => (
    <Card
      key={supplier.supplier}
      className={`border rounded-sm shadow-none cursor-pointer transition-all hover:border-red-300 active:scale-[0.98] ${
        supplier.critical_items > 0 ? 'border-red-200 bg-red-50/30' :
        supplier.products_needing_order > 0 ? 'border-orange-200 bg-orange-50/30' :
        'border-slate-200'
      }`}
      onClick={() => handleSupplierSelect(supplier)}
      data-testid={`supplier-${supplier.supplier}`}
    >
      <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg truncate pr-2">{supplier.supplier}</CardTitle>
          <Truck className="h-5 w-5 text-slate-400 flex-shrink-0" />
        </div>
        {supplier.active_pos_count > 0 && (
          <div className="mt-1.5">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
              title={`${supplier.active_pos_count} encomenda(s) já enviada(s) ou em curso`}
              data-testid={`supplier-active-pos-${supplier.supplier}`}
            >
              <Package className="h-3 w-3" />
              {supplier.active_pos_count} {supplier.active_pos_count === 1 ? 'encomenda em curso' : 'encomendas em curso'}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
        <div className="space-y-2 sm:space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Total Produtos</span>
            <span className="font-mono font-medium">{supplier.total_products}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Precisam Encomenda</span>
            <span className={`font-mono font-medium ${supplier.products_needing_order > 0 ? 'text-orange-600' : ''}`}>
              {supplier.products_needing_order}
            </span>
          </div>
          {supplier.critical_items > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-red-600 flex items-center gap-1">
                <XCircle className="h-4 w-4" />
                Críticos
              </span>
              <span className="font-mono font-medium text-red-600">{supplier.critical_items}</span>
            </div>
          )}
          {supplier.high_risk_items > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-orange-600 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Alto Risco
              </span>
              <span className="font-mono font-medium text-orange-600">{supplier.high_risk_items}</span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          className="w-full mt-4 h-10 sm:h-9"
          onClick={(e) => {
            e.stopPropagation();
            handleSupplierSelect(supplier);
          }}
        >
          Ver Produtos
        </Button>
      </CardContent>
    </Card>
  );

  if (loading && !supplierProducts && suppliers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Order Confirmation View - supports multiple orders (one per supplier)
  if (createdOrders && Object.keys(orderDetailsMap).length > 0) {
    const { orders = [], total_orders = 0, total_items = 0, total_amount = 0 } = createdOrders || {};
    
    // BUG FIX 2026-09-18: Safety check for empty orders array
    if (!orders || orders.length === 0) {
      return (
        <div className="p-4 sm:p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="order-error-page">
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Erro ao carregar encomenda</h2>
              <p className="text-muted-foreground mb-4">A encomenda foi criada mas ocorreu um erro ao carregar os detalhes.</p>
              <Button onClick={handleBackToProducts} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar e tentar novamente
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-[1400px] mx-auto print:p-4" data-testid="order-confirmation-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 print:hidden">
          <div>
            <Button
              variant="ghost"
              onClick={handleBackToProducts}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-emerald-600 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8" />
              {(() => {
                const allSynced = orders.every(o => o.sage_synced || sageSentOrders[o.po_number]?.success);
                const someSynced = orders.some(o => o.sage_synced || sageSentOrders[o.po_number]?.success);
                if (allSynced && total_orders === 1) return 'Encomenda Criada no SAGE';
                if (allSynced && total_orders > 1) return `${total_orders} Encomendas Criadas no SAGE`;
                if (someSynced && total_orders > 1) return `${total_orders} Encomendas Criadas (algumas no SAGE)`;
                if (total_orders === 1) return 'Encomenda Criada';
                return `${total_orders} Encomendas Criadas`;
              })()}
            </h1>
            {total_orders > 1 && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Uma encomenda separada para cada fornecedor (compatível com SAGE 100C)
              </p>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="print-order"
              className="flex-1 sm:flex-none"
            >
              <Printer className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Imprimir</span>
            </Button>
            {/* Send to SAGE button - only show if orders exist and not yet in SAGE */}
            {orders.length > 0 && !orders.every(o => o.sage_synced) && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                onClick={total_orders > 1 ? handleSendAllToSage : () => handleSendToSage(orders[0]?.po_number)}
                disabled={Object.values(sendingSage).some(v => v) || !orders[0]?.po_number}
                data-testid="send-to-sage"
              >
                {Object.values(sendingSage).some(v => v) ? (
                  <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                ) : (
                  <Upload className="h-4 w-4 sm:mr-2" />
                )}
                <span className="hidden sm:inline">{total_orders > 1 ? 'Enviar Todas ao SAGE' : 'Enviar ao SAGE'}</span>
                <span className="sm:hidden">SAGE</span>
              </Button>
            )}
            {total_orders > 1 ? (
              <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
                    data-testid="export-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Exportar Excel (formato SAGE)</span>
                    <span className="sm:hidden">Exportar</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[450px] bg-white mx-4 sm:mx-auto">
                  <DialogHeader>
                    <DialogTitle>Exportar Excel (formato SAGE 100C)</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      Serão exportados {total_orders} ficheiros Excel (um por fornecedor).
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="armazem">Código do Armazém</Label>
                      <Input
                        id="armazem"
                        value={exportArmazem}
                        onChange={(e) => setExportArmazem(e.target.value)}
                        placeholder="001"
                        data-testid="export-armazem"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="formato">Formato do Ficheiro</Label>
                      <Select value={exportFormato} onValueChange={setExportFormato}>
                        <SelectTrigger data-testid="export-formato">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="xlsx">Excel 2007+ (.xlsx)</SelectItem>
                          <SelectItem value="xls">Excel 97-2003 (.xls)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="pt-4">
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={handleExportAll}
                        data-testid="confirm-export"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Exportar {total_orders} Ficheiros
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
                    data-testid="export-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Exportar Excel (formato SAGE)</span>
                    <span className="sm:hidden">Exportar</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[400px] bg-white mx-4 sm:mx-auto">
                  <DialogHeader>
                    <DialogTitle>Exportar Excel (formato SAGE 100C)</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="armazem">Código do Armazém</Label>
                      <Input
                        id="armazem"
                        value={exportArmazem}
                        onChange={(e) => setExportArmazem(e.target.value)}
                        placeholder="001"
                        data-testid="export-armazem"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="formato">Formato do Ficheiro</Label>
                      <Select value={exportFormato} onValueChange={setExportFormato}>
                        <SelectTrigger data-testid="export-formato">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="xlsx">Excel 2007+ (.xlsx)</SelectItem>
                          <SelectItem value="xls">Excel 97-2003 (.xls)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="pt-4">
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleExportExcel(orders[0].po_number)}
                        data-testid="confirm-export"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Exportar Ficheiro
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Summary Banner */}
        <Card className="border-2 border-emerald-200 rounded-sm shadow-none mb-4 sm:mb-6 bg-emerald-50/30">
          <CardContent className="p-4 sm:pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Encomendas</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-emerald-700">{total_orders}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Artigos</p>
                <p className="text-xl sm:text-2xl font-bold font-mono">{total_items}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Fornecedores</p>
                <p className="text-xl sm:text-2xl font-bold font-mono">{total_orders}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Valor Total</p>
                <p className="text-lg sm:text-2xl font-bold font-mono text-emerald-600 truncate" title={`€${total_amount.toFixed(2)}`}>€{total_amount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Individual Orders */}
        {orders.map((order, orderIndex) => {
          const details = orderDetailsMap[order.po_number];
          if (!details) return null;
          const coherence = analyzeSupplierCoherence(details);

          return (
            <Card key={order.po_number} className="border border-slate-200 rounded-sm shadow-none mb-4 sm:mb-6">
              <CardHeader className="bg-slate-50/50 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-bold text-slate-700 flex items-center gap-2 flex-wrap">
                      <Truck className="h-4 w-4 sm:h-5 sm:w-5" />
                      {order.po_number}
                      {/* Show ENF reference if synced to SAGE */}
                      {(order.sage_synced || sageSentOrders[order.po_number]?.success) && (
                        <span className="text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                          {sageSentOrders[order.po_number]?.sage_po_number || order.sage_po_number || 'SAGE ✓'}
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <p className="text-base sm:text-lg font-medium text-red-600">{order.supplier}</p>
                      {coherence.primarySageCode && (
                        <span
                          className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200"
                          title="Código SAGE do fornecedor"
                          data-testid={`po-sage-supplier-code-${order.po_number}`}
                        >
                          SAGE: {coherence.primarySageCode}
                        </span>
                      )}
                      {/* Status badge - use canonical PO status */}
                      {(order.sage_synced || sageSentOrders[order.po_number]?.success) ? (
                        <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                          Encomendada
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                          Pronta para SAGE
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {new Date().toLocaleDateString('pt-PT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-lg sm:text-xl font-bold font-mono text-emerald-600 mt-1">
                      €{order.total_amount.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Coherence alerts — Sessão 25 (PO-001265 root cause prevention) */}
                {coherence.isMixed && (
                  <div
                    className="mt-3 border-2 border-red-300 bg-red-50 rounded-md p-3 flex items-start gap-2"
                    data-testid={`po-mixed-suppliers-alert-${order.po_number}`}
                  >
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-red-700">Encomenda com fornecedores SAGE misturados</p>
                      <p className="text-red-600 mt-1">
                        Esta encomenda contém artigos de {coherence.sageCodes.size} fornecedores diferentes em SAGE
                        ({[...coherence.sageCodes].join(', ')}). Não pode ser enviada ao SAGE assim — use o botão
                        de remover para limpar linhas até ficar apenas um fornecedor, ou divida em encomendas separadas.
                      </p>
                      <div className="mt-2 text-xs font-mono text-red-600">
                        {Object.entries(coherence.breakdown).map(([code, skus]) => (
                          <div key={code}>
                            • {code}: {skus.slice(0, 5).join(', ')}{skus.length > 5 ? ` (+${skus.length - 5})` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {!coherence.isMixed && coherence.missingSageCode && (
                  <div
                    className="mt-3 border-2 border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-2"
                    data-testid={`po-no-sage-code-alert-${order.po_number}`}
                  >
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-700">Sem código SAGE do fornecedor</p>
                      <p className="text-amber-600 mt-1">
                        Nenhuma linha desta encomenda tem `sage_supplier_code` definido.
                        Sincronize fornecedores/produtos com o SAGE antes de tentar enviar.
                      </p>
                    </div>
                  </div>
                )}
                {!coherence.isMixed && coherence.hasTextDivergence && (
                  <div
                    className="mt-3 border border-amber-200 bg-amber-50/60 rounded-md p-2 flex items-start gap-2"
                    data-testid={`po-text-divergence-warning-${order.po_number}`}
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Os nomes textuais dos fornecedores divergem nas linhas, mas o código SAGE é coerente
                      ({coherence.primarySageCode}). O envio para SAGE será efectuado com esse código.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {/* Mobile Card View for Order Items */}
                <div className="sm:hidden divide-y divide-slate-100">
                  {details.items.map((item, index) => (
                    <div key={item.id || index} className="p-3" data-testid={`po-line-mobile-${item.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">#{index + 1}</span>
                            <span className="font-mono text-xs font-semibold text-red-600">{item.sku}</span>
                          </div>
                          <p className="text-sm font-medium truncate mt-1">{item.product_name}</p>
                        </div>
                        {isLineEditable(item, order, details) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 shrink-0"
                            onClick={() => handleRemoveLine(item, order.po_number)}
                            disabled={removingLineId === item.id}
                            title="Remover linha"
                            data-testid={`po-line-remove-mobile-${item.id}`}
                          >
                            {removingLineId === item.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                        <div>
                          <span className="text-muted-foreground">Qtd:</span>
                          <span className="ml-1 font-mono font-semibold">{item.quantity}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Preço:</span>
                          <span className="ml-1 font-mono">€{item.unit_price.toFixed(2)}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-semibold text-emerald-600">€{item.total_amount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Mobile Total */}
                  <div className="p-3 bg-slate-50 flex justify-between items-center">
                    <span className="font-semibold">TOTAL</span>
                    <div className="text-right">
                      <span className="font-mono text-sm mr-4">{details.total_quantity} un.</span>
                      <span className="font-mono font-bold text-emerald-600">€{details.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/30">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground w-12">Linha</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">SKU</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Descrição</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Quantidade</th>
                        <th className="text-center py-3 px-4 font-medium text-muted-foreground">Unidade</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Preço Unit.</th>
                        <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total</th>
                        {isOrderEditable(order, details) && (
                          <th className="text-center py-3 px-4 font-medium text-muted-foreground w-16 print:hidden">Acções</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {details.items.map((item, index) => (
                        <tr key={item.id || index} className="border-b border-slate-100" data-testid={`po-line-${item.id}`}>
                          <td className="py-3 px-4 text-center text-muted-foreground">{index + 1}</td>
                          <td className="py-3 px-4 font-mono text-xs">{item.sku}</td>
                          <td className="py-3 px-4">{item.product_name}</td>
                          <td className="py-3 px-4 font-mono text-right font-semibold">{item.quantity}</td>
                          <td className="py-3 px-4 text-center text-muted-foreground">UN</td>
                          <td className="py-3 px-4 font-mono text-right">€{item.unit_price.toFixed(2)}</td>
                          <td className="py-3 px-4 font-mono text-right font-semibold">€{item.total_amount.toFixed(2)}</td>
                          {isOrderEditable(order, details) && (
                            <td className="py-3 px-4 text-center print:hidden">
                              {isLineEditable(item, order, details) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                  onClick={() => handleRemoveLine(item, order.po_number)}
                                  disabled={removingLineId === item.id}
                                  title="Remover linha"
                                  data-testid={`po-line-remove-${item.id}`}
                                >
                                  {removingLineId === item.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan={3} className="py-3 px-4 text-right">TOTAL</td>
                        <td className="py-3 px-4 font-mono text-right">{details.total_quantity}</td>
                        <td></td>
                        <td></td>
                        <td className="py-3 px-4 font-mono text-right text-emerald-600">
                          €{details.total_amount.toFixed(2)}
                        </td>
                        {isOrderEditable(order, details) && <td className="print:hidden"></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Export & SAGE buttons for this order */}
                <div className="p-3 sm:p-4 border-t border-slate-200 flex items-center justify-between print:hidden">
                  {/* SAGE status - show if synced from backend OR from this session */}
                  {(order.sage_synced || sageSentOrders[order.po_number]?.success) ? (
                    <div className="text-xs flex items-center gap-1 text-blue-600">
                      <CheckCircle className="h-3.5 w-3.5" /> 
                      SAGE: {sageSentOrders[order.po_number]?.sage_po_number || order.sage_po_number || 'Integrada'}
                    </div>
                  ) : sageSentOrders[order.po_number]?.error ? (
                    <div className="text-xs flex items-center gap-1 text-red-500">
                      <AlertCircle className="h-3.5 w-3.5" /> {sageSentOrders[order.po_number].error}
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedExportOrder(order.po_number);
                        handleExportExcel(order.po_number);
                      }}
                    >
                      <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Excel</span>
                    </Button>
                    {/* Only show SAGE button if not yet synced */}
                    {!(order.sage_synced || sageSentOrders[order.po_number]?.success) ? (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleSendToSage(order.po_number)}
                        disabled={sendingSage[order.po_number]}
                      >
                        {sendingSage[order.po_number] ? (
                          <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">Enviar SAGE</span>
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm font-medium">
                        <CheckCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">
                          {sageSentOrders[order.po_number]?.sage_po_number || order.sage_po_number || 'No SAGE'}
                        </span>
                        <span className="sm:hidden">SAGE ✓</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Actions Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-4 sm:mt-6 print:hidden">
          <Button
            variant="outline"
            onClick={handleBackToSuppliers}
            className="order-2 sm:order-1"
          >
            <Truck className="h-4 w-4 mr-2" />
            Nova Encomenda
          </Button>
          <div className="flex gap-2 sm:gap-3 order-1 sm:order-2">
            <Link to="/purchase-orders" className="flex-1 sm:flex-none">
              <Button variant="outline" className="w-full">
                <Eye className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Ver Todas as Encomendas</span>
                <span className="sm:hidden">Ver Todas</span>
              </Button>
            </Link>
            {/* Only show SAGE button if there are unsent orders */}
            {orders.some(o => !o.sage_synced && !sageSentOrders[o.po_number]?.success) && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                onClick={total_orders > 1 ? handleSendAllToSage : () => handleSendToSage(orders[0].po_number)}
                disabled={Object.values(sendingSage).some(v => v)}
              >
                <Upload className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">
                  {orders.filter(o => !o.sage_synced && !sageSentOrders[o.po_number]?.success).length > 1 
                    ? 'Enviar ao SAGE' 
                    : 'Enviar ao SAGE'}
                </span>
                <span className="sm:hidden">SAGE</span>
              </Button>
            )}
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
              onClick={() => setShowExportDialog(true)}
            >
              <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{total_orders > 1 ? 'Exportar Todas' : 'Exportar Excel'}</span>
              <span className="sm:hidden">Excel</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Review Order View — página completa de revisão antes de criar a PO
  if (supplierProducts && showReviewModal) {
    const reviewLines = allSupplierProducts.filter(p => reviewIds.includes(p.product_id));
    const reviewTotalUnits = reviewLines.reduce((s, p) => s + (quantities[p.product_id] || 0), 0);
    const reviewTotalValue = reviewLines.reduce((s, p) => s + (quantities[p.product_id] || 0) * p.unit_price, 0);
    const reviewValidLines = reviewLines.filter(p => (quantities[p.product_id] || 0) > 0).length;

    const handleRemoveReviewLine = (productId) => {
      setSelectedItems(prev => ({ ...prev, [productId]: false }));
      setReviewIds(prev => prev.filter(id => id !== productId));
    };

    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="review-order-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Button
              variant="ghost"
              onClick={() => setShowReviewModal(false)}
              className="mb-2 -ml-2"
              disabled={submitting}
              data-testid="review-cancel-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Sair sem criar
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Eye className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              Rever Encomenda
            </h1>
            <p className="text-base sm:text-lg font-medium text-red-600 mt-1">{supplierProducts.supplier}</p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Ajusta quantidades ou remove linhas. A encomenda só é criada quando confirmares.
            </p>
          </div>
        </div>

        {/* Pending orders warning */}
        {pendingOrders.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50 mb-4 sm:mb-6" data-testid="review-pending-warning">
            <CardContent className="p-3 sm:p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Já existe(m) <strong>{pendingOrders.length}</strong> encomenda(s) pendente(s) deste fornecedor
                (valor total <strong className="font-mono">€{pendingOrders.reduce((sum, o) => sum + o.total_amount, 0).toFixed(2)}</strong>).
                Confirma que não estás a duplicar — as sugestões já descontam as quantidades pendentes.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Summary Banner */}
        <Card className="border-2 border-red-200 rounded-sm shadow-none mb-4 sm:mb-6 bg-red-50/30">
          <CardContent className="p-4 sm:pt-6">
            <div className="grid grid-cols-3 gap-4 sm:gap-6">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Artigos</p>
                <p className="text-xl sm:text-2xl font-bold font-mono" data-testid="review-total-lines">{reviewValidLines}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Unidades</p>
                <p className="text-xl sm:text-2xl font-bold font-mono">{reviewTotalUnits}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Valor Total</p>
                <p className="text-lg sm:text-2xl font-bold font-mono text-red-600 truncate" title={`€${reviewTotalValue.toFixed(2)}`} data-testid="review-total-value">€{reviewTotalValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed lines */}
        <Card className="border border-slate-200 rounded-sm shadow-none mb-4 sm:mb-6">
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="sm:hidden divide-y divide-slate-100" data-testid="review-order-lines">
              {reviewLines.map((p, index) => {
                const qty = quantities[p.product_id] || 0;
                return (
                  <div key={p.product_id} className="p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">#{index + 1}</span>
                          <span className="font-mono text-xs font-semibold text-red-600">{p.sku}</span>
                        </div>
                        <p className="text-sm font-medium mt-1">{p.product_name}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">Stock: {p.current_stock} · €{p.unit_price.toFixed(2)}/un</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 shrink-0"
                        onClick={() => handleRemoveReviewLine(p.product_id)}
                        title="Remover linha"
                        data-testid={`review-remove-mobile-${p.sku}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3 mt-2">
                      <Input
                        type="number"
                        min="0"
                        value={qty}
                        onChange={(e) => handleQuantityChange(p.product_id, e.target.value)}
                        className="w-24 h-10 text-center font-mono"
                        data-testid={`review-qty-mobile-${p.sku}`}
                      />
                      <span className="font-mono font-semibold text-emerald-600">€{(qty * p.unit_price).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/30">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground w-12">Linha</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">SKU</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Descrição</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Stock</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Quantidade</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Unidade</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Preço Unit.</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground w-16">Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewLines.map((p, index) => {
                    const qty = quantities[p.product_id] || 0;
                    return (
                      <tr key={p.product_id} className={`border-b border-slate-100 ${qty === 0 ? 'opacity-50' : ''}`} data-testid={`review-line-${p.sku}`}>
                        <td className="py-3 px-4 text-center text-muted-foreground">{index + 1}</td>
                        <td className="py-3 px-4 font-mono text-xs">{p.sku}</td>
                        <td className="py-3 px-4">{p.product_name}</td>
                        <td className="py-3 px-4 font-mono text-right text-muted-foreground">{p.current_stock}</td>
                        <td className="py-3 px-4 text-right">
                          <Input
                            type="number"
                            min="0"
                            value={qty}
                            onChange={(e) => handleQuantityChange(p.product_id, e.target.value)}
                            className="w-24 text-right font-mono ml-auto"
                            data-testid={`review-qty-${p.sku}`}
                          />
                        </td>
                        <td className="py-3 px-4 text-center text-muted-foreground">UN</td>
                        <td className="py-3 px-4 font-mono text-right">€{p.unit_price.toFixed(2)}</td>
                        <td className="py-3 px-4 font-mono text-right font-semibold">€{(qty * p.unit_price).toFixed(2)}</td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => handleRemoveReviewLine(p.product_id)}
                            title="Remover linha"
                            data-testid={`review-remove-${p.sku}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-semibold">
                    <td colSpan={4} className="py-3 px-4 text-right">TOTAL</td>
                    <td className="py-3 px-4 font-mono text-right">{reviewTotalUnits}</td>
                    <td></td>
                    <td></td>
                    <td className="py-3 px-4 font-mono text-right text-red-600">€{reviewTotalValue.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* SAGE option + actions */}
        <Card className="border-2 border-red-200 rounded-sm shadow-none bg-red-50/30">
          <CardContent className="p-4 sm:p-6">
            {/* Fiscal Configuration Warning (Phase 1) */}
            {sendToSage && selectedSupplier?.sage_code && (() => {
              const fiscalData = supplierFiscalStatus[selectedSupplier.sage_code];
              const fiscalStatus = fiscalData?.fiscal_status;
              
              if (fiscalStatus && fiscalStatus !== 'valid') {
                return (
                  <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50" data-testid="fiscal-warning">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Configuração fiscal incompleta
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {fiscalData?.fiscal_validation?.message || 
                            'O fornecedor não tem configuração fiscal completa. A encomenda será bloqueada ao tentar enviar ao SAGE.'}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                          onClick={() => setFiscalConfigModal({
                            open: true,
                            supplierCode: selectedSupplier.sage_code,
                            supplierName: supplierProducts?.supplier || selectedSupplier.name
                          })}
                          data-testid="configure-fiscal-btn"
                        >
                          <Settings className="h-3 w-3 mr-1" />
                          Configurar Fornecedor
                        </Button>
                      </div>
                      <FiscalStatusBadge status={fiscalStatus} />
                    </div>
                  </div>
                );
              }
              
              // Show green badge if configured
              if (fiscalStatus === 'valid') {
                return (
                  <div className="mb-4 flex items-center justify-between p-2 rounded-md border border-green-200 bg-green-50" data-testid="fiscal-valid">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-xs text-green-700">Configuração fiscal OK</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-green-700 hover:bg-green-100"
                      onClick={() => setFiscalConfigModal({
                        open: true,
                        supplierCode: selectedSupplier.sage_code,
                        supplierName: supplierProducts?.supplier || selectedSupplier.name
                      })}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Ver
                    </Button>
                  </div>
                );
              }
              
              return null;
            })()}
            
            <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-white p-3 cursor-pointer hover:bg-slate-50 mb-4">
              <input
                type="checkbox"
                checked={sendToSage}
                onChange={(e) => setSendToSage(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-red-600"
                data-testid="send-to-sage-checkbox"
              />
              <span className="text-sm">
                <strong>Enviar já para o SAGE</strong>
                <span className="block text-xs text-muted-foreground">Cria imediatamente o documento de encomenda no SAGE após criar a PO — sem passo extra.</span>
              </span>
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Button
                variant="outline"
                className="h-11 order-2 sm:order-1"
                onClick={() => setShowReviewModal(false)}
                disabled={submitting}
                data-testid="review-cancel-btn-bottom"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Sair sem criar
              </Button>
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 h-12 sm:h-11 px-4 sm:px-6 order-1 sm:order-2"
                onClick={confirmCreateOrder}
                disabled={submitting || reviewValidLines === 0}
                data-testid="review-confirm-btn"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {submitting ? 'A criar...' : `Confirmar e criar encomenda${sendToSage ? ' + SAGE' : ''}`}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Fiscal Configuration Modal */}
        <SupplierFiscalConfig
          isOpen={fiscalConfigModal.open}
          onClose={() => setFiscalConfigModal({ open: false, supplierCode: null, supplierName: null })}
          supplierCode={fiscalConfigModal.supplierCode}
          supplierName={fiscalConfigModal.supplierName}
          token={getAuthToken()}
          onSave={(data) => {
            // Update local cache
            if (data.supplier_code) {
              setSupplierFiscalStatus(prev => ({ ...prev, [data.supplier_code]: data }));
            }
            toast.success('Configuração fiscal guardada');
          }}
        />
      </div>
    );
  }

  // Supplier Products View
  if (supplierProducts) {
    const { totalItems, totalValue } = calculateTotals();
    
    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-[1600px] mx-auto" data-testid="supplier-products-page">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <Button
              variant="ghost"
              onClick={handleBackToSuppliers}
              className="mb-2 -ml-2"
              data-testid="back-to-suppliers"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Voltar aos Fornecedores</span>
              <span className="sm:hidden">Voltar</span>
            </Button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{supplierProducts.supplier}</h1>
              {/* Fiscal Configuration Button */}
              {selectedSupplier?.sage_code && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-slate-300 hover:border-blue-400 hover:bg-blue-50"
                  onClick={async () => {
                    // Fetch fiscal status before opening modal
                    await fetchFiscalStatus(selectedSupplier.sage_code);
                    setFiscalConfigModal({
                      open: true,
                      supplierCode: selectedSupplier.sage_code,
                      supplierName: supplierProducts.supplier
                    });
                  }}
                  data-testid="supplier-fiscal-config-btn"
                >
                  <Settings className="h-3 w-3 mr-1" />
                  Config. Fiscal
                  {supplierFiscalStatus[selectedSupplier.sage_code] && (
                    <span className="ml-1.5">
                      {supplierFiscalStatus[selectedSupplier.sage_code].fiscal_status === 'valid' ? '🟢' : 
                       supplierFiscalStatus[selectedSupplier.sage_code].fiscal_status === 'incomplete' ? '🟡' : '🔴'}
                    </span>
                  )}
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {supplierProducts.total_products} produtos • {supplierProducts.products_needing_order} precisam encomenda
            </p>
          </div>
          {/* Top action bar — evita scroll até ao fundo em fornecedores grandes */}
          {totalItems > 0 && (
            <div className="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-4 py-2.5 bg-red-50/95 backdrop-blur border border-red-200 sm:rounded-md flex items-center justify-between gap-3" data-testid="top-order-bar">
              <p className="text-sm min-w-0 truncate">
                <strong>{totalItems}</strong> produto{totalItems !== 1 ? 's' : ''} · <span className="font-mono font-semibold">€{totalValue.toFixed(2)}</span>
              </p>
              <Button
                className="bg-red-600 hover:bg-red-700 h-10 flex-shrink-0"
                onClick={handleSubmitOrder}
                disabled={submitting}
                data-testid="top-create-order-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Criar Encomenda
              </Button>
            </div>
          )}
        </div>

        {/* Alert Banner for Pending Orders */}
        {pendingOrders.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50 mb-4 sm:mb-6" data-testid="pending-orders-alert">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="p-1.5 sm:p-2 bg-amber-100 rounded-full flex-shrink-0">
                  <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-amber-800 text-sm sm:text-base">
                    Encomendas pendentes
                  </h3>
                  <p className="text-xs sm:text-sm text-amber-700 mt-1">
                    <strong>{pendingOrders.length}</strong> encomenda(s) pendente(s)
                  </p>
                  
                  {/* List of pending orders - Mobile optimized */}
                  <div className="mt-2 sm:mt-3 space-y-2">
                    {pendingOrders.slice(0, 2).map((order) => (
                      <Link
                        key={order.po_number}
                        to={`/purchase-orders?po=${encodeURIComponent(order.po_number)}`}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-amber-100/50 rounded px-2 sm:px-3 py-2 text-xs sm:text-sm gap-1 sm:gap-0 hover:bg-amber-200/60 transition-colors cursor-pointer"
                        title="Abrir esta encomenda"
                        data-testid={`open-pending-po-${order.po_number}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="bg-white border-amber-400 text-amber-700 text-xs">
                            {order.po_number}
                          </Badge>
                          <span className="text-amber-700">{order.items.length} itens</span>
                          <span className="text-amber-500 underline decoration-dotted">ver encomenda →</span>
                        </div>
                        <span className="font-mono font-semibold text-amber-800">€{order.total_amount.toFixed(2)}</span>
                      </Link>
                    ))}
                    {pendingOrders.length > 2 && (
                      <p className="text-xs text-amber-600">... +{pendingOrders.length - 2} encomenda(s)</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="border border-slate-200 rounded-sm shadow-none">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Produtos</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono">{supplierProducts.total_products}</p>
                </div>
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-orange-200 rounded-sm shadow-none bg-orange-50/30">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Precisam</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono text-orange-600">{supplierProducts.products_needing_order}</p>
                </div>
                <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-red-200 rounded-sm shadow-none bg-red-50/30">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Críticos</p>
                  <p className="text-xl sm:text-2xl font-bold font-mono text-red-600">{supplierProducts.critical_items}</p>
                </div>
                <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-emerald-200 rounded-sm shadow-none bg-emerald-50/30">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Valor</p>
                  <p className="text-lg sm:text-2xl font-bold font-mono text-emerald-600">€{supplierProducts.total_suggested_value.toFixed(2)}</p>
                </div>
                <Calculator className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Selection Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll} className="text-xs sm:text-sm">
              Selecionar Todos
            </Button>
            <Button variant="outline" size="sm" onClick={handleSelectNeedsOrder} className="text-xs sm:text-sm">
              Sugeridos
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleDeselectAll}>
              Limpar Seleção
            </Button>
          </div>
          
          {/* Add Manual Product */}
          <Popover open={searchOpen} onOpenChange={(open) => {
            setSearchOpen(open);
            if (!open) {
              // Clear search when closing
              setSearchQuery('');
              setAllProducts([]);
            }
          }}>
            <PopoverTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-manual-product">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Produto
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[500px] p-0 bg-white" align="end">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Pesquisar por SKU ou nome... (mín. 2 caracteres)"
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  data-testid="manual-product-search"
                />
                <CommandList>
                  {searchingProducts ? (
                    <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      A pesquisar…
                    </div>
                  ) : searchQuery.trim().length < 2 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>Digite pelo menos 2 caracteres para pesquisar</p>
                      <p className="text-xs mt-1">Pesquisa em +14.000 produtos</p>
                    </div>
                  ) : !hasAvailableProducts ? (
                    <CommandEmpty>
                      {filteredProductsForAdd.length === 0 && allProducts.length > 0
                        ? 'Todos os produtos encontrados já estão na lista.'
                        : 'Nenhum produto encontrado com esse termo.'}
                    </CommandEmpty>
                  ) : (
                    <CommandGroup heading={`${filteredProductsForAdd.length} produto${filteredProductsForAdd.length !== 1 ? 's' : ''} encontrado${filteredProductsForAdd.length !== 1 ? 's' : ''}${searchTotal > filteredProductsForAdd.length ? ` (de ${searchTotal} no total)` : ''}`}>
                      {filteredProductsForAdd.map((product) => (
                        <CommandItem
                          key={product.id}
                          value={product.id}
                          onSelect={() => handleAddManualProduct(product)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-semibold text-red-600">{product.sku}</span>
                                {/* Show preferred supplier badge if different from current supplier */}
                                {product.supplier && product.supplier !== supplierProducts?.supplier && (
                                  <span 
                                    className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200"
                                    title={`Fornecedor preferencial: ${product.supplier}. Ao adicionar, permanece na encomenda de ${supplierProducts?.supplier}.`}
                                  >
                                    Pref: {product.supplier?.length > 15 ? product.supplier.substring(0, 15) + '...' : product.supplier}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm">{product.name}</p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="font-mono text-sm font-semibold">€{product.unit_price?.toFixed(2) || 'N/D'}</p>
                              <p className="text-xs text-muted-foreground">
                                Stock: {product.current_stock}
                              </p>
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Manual Products Added Notice */}
        {manualProducts.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-sm text-blue-800">
              <Plus className="h-4 w-4 inline mr-1" />
              {manualProducts.length} produto{manualProducts.length > 1 ? 's' : ''} adicionado{manualProducts.length > 1 ? 's' : ''} manualmente
            </p>
          </div>
        )}

        {/* Draft continuation banner */}
        {draftData && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="draft-continuation-banner">
            <div className="flex items-center justify-between">
              <p className="text-sm text-amber-800">
                <Clock className="h-4 w-4 inline mr-1" />
                <strong>A continuar rascunho {draftData.po_number}</strong> — {draftData.total_items} artigo{draftData.total_items !== 1 ? 's' : ''}, €{draftData.total_value?.toFixed(2) || '0.00'}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                onClick={() => {
                  setDraftData(null);
                  toast.info('Modo de rascunho cancelado — será criada uma nova encomenda');
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Criar nova em vez
              </Button>
            </div>
          </div>
        )}

        {/* Cart convergence banner */}
        {cartItemsForSupplier.length > 0 && !createdOrders && (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="cart-convergence-banner">
            <ShoppingCart className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              <strong>{cartItemsForSupplier.length} artigo{cartItemsForSupplier.length !== 1 ? 's' : ''} do carrinho</strong> deste fornecedor
              {cartItemsForSupplier.length !== 1 ? ' foram incluídos' : ' foi incluído'} e pré-selecionado{cartItemsForSupplier.length !== 1 ? 's' : ''} nesta encomenda.
              Ao criar a encomenda, {cartItemsForSupplier.length !== 1 ? 'são removidos' : 'é removido'} do carrinho automaticamente.
            </p>
          </div>
        )}

        {/* Advanced Filters Bar */}
        <Card className="border border-slate-200 rounded-sm shadow-none mb-4" data-testid="advanced-filters-bar">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col gap-3">
              {/* Search Bar - First Row */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Pesquisar por SKU, designação..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-sm"
                    data-testid="product-search-input"
                  />
                  {productSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setProductSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                    >
                      <X className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filters Row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Filter Icon + Label */}
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <Filter className="h-4 w-4" />
                <span>Filtrar por:</span>
              </div>
              
              {/* Risk Level Multi-Select */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground mr-1">Risco:</span>
                <button
                  type="button"
                  onClick={() => toggleRiskFilter('critical')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    riskFilters.includes('critical')
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'
                  }`}
                  data-testid="filter-risk-critical"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Crítico
                </button>
                <button
                  type="button"
                  onClick={() => toggleRiskFilter('zero_stock_alert')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    riskFilters.includes('zero_stock_alert')
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'
                  }`}
                  data-testid="filter-risk-zero-stock-alert"
                  title="Produtos com stock=0, procura relevante, mas sem parâmetros de reposição automática"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Sem Stock
                </button>
                <button
                  type="button"
                  onClick={() => toggleRiskFilter('high')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    riskFilters.includes('high')
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
                  }`}
                  data-testid="filter-risk-high"
                >
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  Alto
                </button>
                <button
                  type="button"
                  onClick={() => toggleRiskFilter('medium')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    riskFilters.includes('medium')
                      ? 'bg-yellow-600 text-white border-yellow-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-yellow-300'
                  }`}
                  data-testid="filter-risk-medium"
                >
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Atenção
                </button>
                <button
                  type="button"
                  onClick={() => toggleRiskFilter('low')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    riskFilters.includes('low')
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                  }`}
                  data-testid="filter-risk-low"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Saudável
                </button>
              </div>
              
              {/* Separator */}
              <div className="hidden sm:block w-px h-6 bg-slate-200" />
              
              {/* Zero Stock Toggle */}
              <button
                type="button"
                onClick={() => setShowZeroStockOnly(v => !v)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                  showZeroStockOnly
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
                data-testid="filter-zero-stock"
              >
                <XCircle className="h-3 w-3" />
                Stock = 0
              </button>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAdvancedFilters}
                  className="px-2.5 py-1 rounded-full text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-1"
                  data-testid="clear-advanced-filters"
                >
                  <X className="h-3 w-3" />
                  Limpar filtros
                </button>
              )}
              
              {/* Results count */}
              <div className="sm:ml-auto text-xs text-muted-foreground">
                {hasActiveFilters ? (
                  <span>
                    A mostrar <strong className="text-slate-800">{filteredSupplierProducts.length}</strong> de {allSupplierProducts.length} produtos
                  </span>
                ) : (
                  <span>{allSupplierProducts.length} produtos</span>
                )}
              </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="border border-slate-200 rounded-sm shadow-none mb-4 sm:mb-6">
          <CardContent className="p-0">
            {/* Mobile Card View */}
            <div className="sm:hidden divide-y divide-slate-100">
              {filteredSupplierProducts.map((product) => {
                const isSelected = selectedItems[product.product_id];
                const qty = quantities[product.product_id] || 0;
                const itemTotal = isSelected ? qty * product.unit_price : 0;
                const isManual = product.isManual;
                
                return (
                  <div
                    key={product.product_id}
                    className={`p-3 ${isSelected ? 'bg-red-50/50' : ''} ${isManual ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected || false}
                        onCheckedChange={() => handleToggleItem(product.product_id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-xs font-semibold text-red-600">{product.sku}</span>
                          {getRiskBadge(product.risk_level)}
                          {(product.pending_quantity || 0) > 0 && (
                            getPendingPoLink(product.product_id) ? (
                              <Link to={getPendingPoLink(product.product_id)} onClick={(e) => e.stopPropagation()}>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-700 border-blue-300 cursor-pointer hover:bg-blue-100 underline decoration-dotted" title={getPendingPoTitle(product.product_id, product.pending_quantity)} data-testid={`on-order-link-mobile-${product.sku}`}>
                                  🚚 {product.pending_quantity} a caminho
                                </Badge>
                              </Link>
                            ) : (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-700 border-blue-300" title={`${product.pending_quantity} unidade(s) encomendada(s) por receber`}>
                                🚚 {product.pending_quantity} a caminho
                              </Badge>
                            )
                          )}
                          {cartMap[product.product_id] && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-amber-50 text-amber-700 border-amber-300">
                              <ShoppingCart className="h-2.5 w-2.5 mr-0.5" />
                              Carrinho ({cartMap[product.product_id].quantity})
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate">{product.product_name}</p>
                        
                        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Stock:</span>
                            <span className={`ml-1 font-mono ${product.current_stock <= product.reorder_point ? 'text-red-600 font-semibold' : ''}`}>
                              {product.current_stock}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Min:</span>
                            <span className="ml-1 font-mono">{product.reorder_point}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Preço:</span>
                            <span className="ml-1 font-mono">€{product.unit_price.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => {
                                const currentQty = quantities[product.product_id] || 0;
                                if (currentQty > 0) {
                                  handleQuantityChange(product.product_id, currentQty - 1);
                                }
                              }}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <Input
                              type="number"
                              min={0}
                              value={qty}
                              onChange={(e) => handleQuantityChange(product.product_id, parseInt(e.target.value) || 0)}
                              className="w-16 h-8 text-center font-mono text-sm"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => {
                                const currentQty = quantities[product.product_id] || 0;
                                handleQuantityChange(product.product_id, currentQty + 1);
                              }}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="text-right">
                            <span className="font-mono font-semibold text-emerald-600">€{itemTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground w-12"></th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">SKU</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Produto</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Fornecedor</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Stock</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Min.</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Risco</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Preço</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">MOQ</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground w-32">Qtd</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Total</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSupplierProducts.map((product) => {
                    const isSelected = selectedItems[product.product_id];
                    const qty = quantities[product.product_id] || 0;
                    const itemTotal = isSelected ? qty * product.unit_price : 0;
                    const isManual = product.isManual;
                    const effectiveSupplier = getEffectiveSupplier(product);
                    const suppliers = productSuppliers[product.sku] || [];
                    const hasAlternatives = suppliers.length > 1;
                    
                    return (
                      <tr
                        key={product.product_id}
                        className={`border-b border-slate-100 ${isSelected ? 'bg-red-50/30' : 'hover:bg-slate-50/50'} ${product.needs_order || isManual ? '' : 'opacity-60'} ${isManual ? 'bg-blue-50/30' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <Checkbox
                            checked={isSelected || false}
                            onCheckedChange={() => handleToggleItem(product.product_id)}
                            data-testid={`select-${product.sku}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">
                          {product.sku}
                          {isManual && !product.fromCart && <span className="ml-1 text-blue-600">*</span>}
                          {/* Badge for products with different preferred supplier */}
                          {isManual && product.supplier && product.supplier !== supplierProducts?.supplier && (
                            <Badge 
                              variant="outline" 
                              className="ml-1.5 text-[9px] px-1 py-0 h-4 bg-amber-50 text-amber-600 border-amber-200" 
                              title={`Fornecedor preferencial: ${product.supplier}. Adicionado manualmente a esta encomenda.`}
                              data-testid={`alt-supplier-badge-${product.sku}`}
                            >
                              Pref: outro
                            </Badge>
                          )}
                          {(product.pending_quantity || 0) > 0 && (
                            getPendingPoLink(product.product_id) ? (
                              <Link to={getPendingPoLink(product.product_id)} onClick={(e) => e.stopPropagation()}>
                                <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-700 border-blue-300 cursor-pointer hover:bg-blue-100 underline decoration-dotted" title={getPendingPoTitle(product.product_id, product.pending_quantity)} data-testid={`on-order-badge-${product.sku}`}>
                                  🚚 {product.pending_quantity}
                                </Badge>
                              </Link>
                            ) : (
                              <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-700 border-blue-300" title={`${product.pending_quantity} unidade(s) encomendada(s) por receber`} data-testid={`on-order-badge-${product.sku}`}>
                                🚚 {product.pending_quantity}
                              </Badge>
                            )
                          )}
                          {cartMap[product.product_id] && (
                            <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 h-4 bg-amber-50 text-amber-700 border-amber-300" data-testid={`cart-badge-${product.sku}`}>
                              <ShoppingCart className="h-2.5 w-2.5 mr-0.5" />
                              Carrinho ({cartMap[product.product_id].quantity})
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <button 
                            onClick={() => openProductDetailPanel(product)}
                            className="text-left hover:text-red-600 hover:underline cursor-pointer"
                            title="Ver detalhes do produto"
                          >
                            {product.product_name}
                          </button>
                        </td>
                        {/* Supplier column with alternatives */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <span className={`text-xs truncate max-w-[120px] ${effectiveSupplier.isOverride ? 'text-blue-600 font-medium' : 'text-muted-foreground'}`}>
                              {effectiveSupplier.name?.substring(0, 20) || 'N/A'}
                              {effectiveSupplier.name?.length > 20 && '...'}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                fetchProductSuppliers(product.sku);
                                setShowSupplierModal(product);
                              }}
                              title="Ver fornecedores alternativos"
                            >
                              {loadingSuppliers[product.sku] ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <Users className="h-3 w-3 text-muted-foreground hover:text-red-600" />
                              )}
                            </Button>
                            {effectiveSupplier.isOverride && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-blue-50 text-blue-600 border-blue-200">
                                Alt
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-right">
                          <span className={product.current_stock <= product.reorder_point ? 'text-red-600 font-semibold' : ''}>
                            {product.current_stock}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-right text-muted-foreground">{product.reorder_point}</td>
                        <td className="py-3 px-4 text-center">{getRiskBadge(product.risk_level)}</td>
                        <td className="py-3 px-4 font-mono text-right">€{product.unit_price.toFixed(2)}</td>
                        <td className="py-3 px-4 font-mono text-right text-muted-foreground">{product.moq}</td>
                        <td className="py-3 px-4 text-right">
                          <Input
                            type="number"
                            min="0"
                            step={product.moq}
                            value={qty}
                            onChange={(e) => handleQuantityChange(product.product_id, e.target.value)}
                            className="w-24 text-right font-mono"
                            disabled={!isSelected}
                            data-testid={`qty-${product.sku}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-right font-medium">
                          {isSelected && qty > 0 ? `€${itemTotal.toFixed(2)}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {isManual && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveManualProduct(product.product_id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Supplier Selection Modal */}
        <Dialog open={showSupplierModal !== null} onOpenChange={(open) => !open && setShowSupplierModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Fornecedores Alternativos
              </DialogTitle>
            </DialogHeader>
            {showSupplierModal && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-sm border">
                  <p className="font-mono text-sm text-red-600">{showSupplierModal.sku}</p>
                  <p className="text-sm font-medium">{showSupplierModal.product_name}</p>
                </div>
                
                {loadingSuppliers[showSupplierModal.sku] ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {(productSuppliers[showSupplierModal.sku] || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nenhum fornecedor alternativo encontrado
                      </p>
                    ) : (
                      (productSuppliers[showSupplierModal.sku] || []).map((supplier) => {
                        const isCurrentSupplier = supplierProducts?.supplier === supplier.supplier_name;
                        const isOverride = supplierOverrides[showSupplierModal.sku]?.supplier_code === supplier.supplier_code;
                        
                        return (
                          <div
                            key={supplier.id}
                            className={`p-3 rounded-sm border cursor-pointer transition-colors ${
                              isOverride ? 'bg-blue-50 border-blue-300' :
                              isCurrentSupplier ? 'bg-green-50 border-green-300' :
                              'hover:bg-slate-50 border-slate-200'
                            }`}
                            onClick={() => handleSupplierOverride(showSupplierModal.sku, supplier)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{supplier.supplier_name}</span>
                                  {supplier.is_preferred && (
                                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" title="Fornecedor preferencial" />
                                  )}
                                  {supplier.is_best_price && (
                                    <Badge className="text-[10px] px-1 py-0 h-4 bg-green-100 text-green-700 border-green-300">
                                      💰 Melhor Preço
                                    </Badge>
                                  )}
                                  {isCurrentSupplier && !isOverride && (
                                    <Badge className="text-[10px] px-1 py-0 h-4 bg-slate-100 text-slate-700">
                                      Actual
                                    </Badge>
                                  )}
                                  {isOverride && (
                                    <Badge className="text-[10px] px-1 py-0 h-4 bg-blue-100 text-blue-700">
                                      Selecionado
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                  <span>Cód: {supplier.supplier_code}</span>
                                  {supplier.lead_time_days > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {supplier.lead_time_days} dias
                                    </span>
                                  )}
                                  
                                  {/* Price display with edit capability */}
                                  {editingPrice === supplier.id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <span className="text-slate-500">€</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="w-20 px-1 py-0.5 text-xs border rounded font-mono"
                                        value={editPriceValue}
                                        onChange={(e) => setEditPriceValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSavePrice(supplier);
                                          if (e.key === 'Escape') { setEditingPrice(null); setEditPriceValue(''); }
                                        }}
                                        autoFocus
                                      />
                                      <button
                                        className="p-0.5 text-green-600 hover:bg-green-50 rounded"
                                        onClick={(e) => { e.stopPropagation(); handleSavePrice(supplier); }}
                                        disabled={savingPrice}
                                      >
                                        <Save className="h-3 w-3" />
                                      </button>
                                      <button
                                        className="p-0.5 text-slate-400 hover:bg-slate-100 rounded"
                                        onClick={(e) => { e.stopPropagation(); setEditingPrice(null); setEditPriceValue(''); }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <span 
                                      className={`flex items-center gap-1 cursor-pointer hover:underline ${supplier.is_best_price ? 'text-green-600' : 'text-slate-600'}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingPrice(supplier.id);
                                        setEditPriceValue(supplier.last_purchase_price?.toString() || '');
                                      }}
                                      title="Clique para editar preço"
                                    >
                                      {supplier.last_purchase_price ? (
                                        <span className="font-mono font-medium">
                                          €{supplier.last_purchase_price.toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-slate-400 italic">sem preço</span>
                                      )}
                                      <Pencil className="h-2.5 w-2.5 text-slate-400" />
                                    </span>
                                  )}
                                  
                                  {supplier.last_purchase_date && editingPrice !== supplier.id && (
                                    <span className="text-slate-400" title={supplier.price_source === 'manual' ? 'Introduzido manualmente' : supplier.price_source === 'po_stockflow' ? 'De encomenda StockFlow' : ''}>
                                      ({new Date(supplier.last_purchase_date).toLocaleDateString('pt-PT', { month: 'short', year: '2-digit' })})
                                      {supplier.price_source === 'manual' && <span className="ml-1 text-amber-500">✎</span>}
                                    </span>
                                  )}
                                </div>
                                {supplier.supplier_reference && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Ref: {supplier.supplier_reference}
                                  </p>
                                )}
                              </div>
                              {!supplier.is_active && (
                                <Badge variant="destructive" className="text-[10px]">Inactivo</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setShowSupplierModal(null)}>
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Order Summary & Submit */}
        <Card className="border-2 border-red-200 rounded-sm shadow-none bg-red-50/30">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="flex items-center justify-between sm:block space-y-0 sm:space-y-1">
                <h3 className="text-base sm:text-lg font-semibold">Resumo da Encomenda</h3>
                <p className="text-sm text-muted-foreground">
                  {totalItems} produto{totalItems !== 1 ? 's' : ''} selecionado{totalItems !== 1 ? 's' : ''}
                  {manualProducts.length > 0 && ` (${manualProducts.filter(p => selectedItems[p.product_id]).length} manual)`}
                </p>
              </div>
              <div className="flex items-center justify-between sm:gap-6">
                <div className="text-left sm:text-right space-y-0 sm:space-y-1">
                  <p className="text-xs sm:text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-2xl sm:text-3xl font-bold font-mono">€{totalValue.toFixed(2)}</p>
                </div>
                <Button
                  size="lg"
                  className={`${draftData ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'} sm:ml-6 h-12 sm:h-11 px-4 sm:px-6`}
                  onClick={handleSubmitOrder}
                  disabled={totalItems === 0 || submitting}
                  data-testid="submit-bulk-order"
                >
                  <ShoppingCart className="h-5 w-5 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {submitting 
                      ? (draftData ? 'A atualizar...' : 'A criar...') 
                      : (draftData ? `Atualizar ${draftData.po_number}` : 'Criar Encomenda')
                    }
                  </span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* O fluxo de revisão é agora uma página completa (ver branch showReviewModal acima) */}
        
        {/* Product Detail Side Panel - inside supplierProducts view */}
        <Sheet open={productDetailPanel.open} onOpenChange={(open) => !open && closeProductDetailPanel()}>
          <SheetContent side="right" className="w-full sm:w-[540px] sm:max-w-xl overflow-y-auto">
            <SheetHeader className="mb-4">
              <SheetTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Detalhes do Produto
              </SheetTitle>
            </SheetHeader>
            
            {productDetailPanel.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : productDetailPanel.data ? (
              <div className="space-y-6">
                {/* Product Header */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono text-xs">{productDetailPanel.data.sku}</Badge>
                    {productDetailPanel.data.ai_tracking_enabled && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">PI Activo</Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold">{productDetailPanel.data.name}</h3>
                  <p className="text-sm text-muted-foreground">{productDetailPanel.data.category} — {productDetailPanel.data.supplier}</p>
                </div>

                {/* Stock Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Stock Total</p>
                    <p className="text-2xl font-bold">{productDetailPanel.data.stock_real ?? productDetailPanel.data.current_stock ?? 0}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Reorder Point</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {productDetailPanel.data.manual_reorder_point ?? productDetailPanel.data.reorder_point ?? '—'}
                    </p>
                  </div>
                  {productDetailPanel.data.moq && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">MOQ</p>
                      <p className="text-lg font-semibold">{productDetailPanel.data.moq}</p>
                    </div>
                  )}
                  {productDetailPanel.data.lead_time_days && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Lead Time</p>
                      <p className="text-lg font-semibold">{productDetailPanel.data.lead_time_days} dias</p>
                    </div>
                  )}
                </div>

                {/* Stock by Warehouse */}
                {productDetailPanel.data.stock_by_warehouse && Object.keys(productDetailPanel.data.stock_by_warehouse).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Warehouse className="h-4 w-4" />
                      Stock por Armazém
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(productDetailPanel.data.stock_by_warehouse).map(([wh, qty]) => (
                        <div key={wh} className="p-2 bg-slate-50 rounded text-center">
                          <p className="text-xs text-muted-foreground">{wh}</p>
                          <p className="font-semibold">{qty}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing Info from Ficha */}
                {productDetailPanel.data.ficha && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Calculator className="h-4 w-4" />
                      Preços
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {productDetailPanel.data.ficha.avg_cost != null && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600">Custo Médio (PCMP)</p>
                          <p className="font-semibold text-blue-700">{productDetailPanel.data.ficha.avg_cost.toFixed(2)}€</p>
                        </div>
                      )}
                      {productDetailPanel.data.ficha.pvp != null && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600">PVP</p>
                          <p className="font-semibold text-green-700">{productDetailPanel.data.ficha.pvp.toFixed(2)}€</p>
                        </div>
                      )}
                      {productDetailPanel.data.ficha.supplier_price != null && (
                        <div className="p-3 bg-orange-50 rounded-lg col-span-2">
                          <p className="text-xs text-orange-600">Preço Fornecedor (Última ENF)</p>
                          <p className="font-semibold text-orange-700">{productDetailPanel.data.ficha.supplier_price.toFixed(2)}€</p>
                          {productDetailPanel.data.ficha.supplier_price_doc_ref && (
                            <p className="text-xs text-orange-500 mt-1">Ref: {productDetailPanel.data.ficha.supplier_price_doc_ref}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Forecast / Demand if available */}
                {(productDetailPanel.data.avg_daily_demand || productDetailPanel.data.days_of_stock) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Previsão
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {productDetailPanel.data.avg_daily_demand && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Consumo Médio/Dia</p>
                          <p className="font-semibold">{productDetailPanel.data.avg_daily_demand.toFixed(2)}</p>
                        </div>
                      )}
                      {productDetailPanel.data.days_of_stock != null && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Dias de Stock</p>
                          <p className="font-semibold">{Math.round(productDetailPanel.data.days_of_stock)} dias</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/inventory/${productDetailPanel.data.id}`} target="_blank">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Abrir Ficha Completa
                    </Link>
                  </Button>
                </div>
              </div>
            ) : productDetailPanel.product ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Não foi possível carregar os detalhes</p>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
        
        {/* Fiscal Configuration Modal (for supplier view) */}
        <SupplierFiscalConfig
          isOpen={fiscalConfigModal.open}
          onClose={() => setFiscalConfigModal({ open: false, supplierCode: null, supplierName: null })}
          supplierCode={fiscalConfigModal.supplierCode}
          supplierName={fiscalConfigModal.supplierName}
          token={getAuthToken()}
          onSave={(data) => {
            // Update local cache
            if (data.supplier_code) {
              setSupplierFiscalStatus(prev => ({ ...prev, [data.supplier_code]: data }));
            }
            toast.success('Configuração fiscal guardada');
          }}
        />
      </div>
    );
  }

  // Suppliers List View
  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1600px] mx-auto pb-20" data-testid="supplier-orders-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Encomendas por Fornecedor</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Selecione um fornecedor para criar encomenda · {suppliers.length} fornecedores activos
          {showOnlyPending && (
            <span className="ml-1">
              · <span className="text-orange-600 font-medium">{suppliers.filter(s => (s.products_needing_order || 0) > 0).length} com pendências</span>
            </span>
          )}
        </p>
        {/* Pending-only toggle + supplier search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
          <button
            type="button"
            onClick={() => setShowOnlyPending(v => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 w-fit ${
              showOnlyPending
                ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
            }`}
            data-testid="toggle-only-pending"
            title="Mostrar apenas fornecedores com produtos a precisar de encomenda"
          >
            <AlertTriangle className="h-3 w-3" />
            {showOnlyPending ? 'Só com pendências' : 'Todos os fornecedores'}
          </button>
          <Input
            placeholder="Pesquisar fornecedor..."
            value={supplierSearch}
            onChange={(e) => setSupplierSearch(e.target.value)}
            className="max-w-xs h-9"
            data-testid="supplier-search-input"
          />
          <Select value={supplierSort} onValueChange={setSupplierSort}>
            <SelectTrigger className="w-full sm:w-[220px] h-9" data-testid="supplier-sort-select">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="name">Nome (A-Z)</SelectItem>
              <SelectItem value="needing">Mais a precisar encomenda</SelectItem>
              <SelectItem value="critical">Mais artigos críticos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Department Filter */}
        {departmentNames.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Filter className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <button
              onClick={() => setDepartmentFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                departmentFilter === 'all'
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              Todos
            </button>
            {departmentNames.map(name => (
              <button
                key={name}
                onClick={() => setDepartmentFilter(departmentFilter === name ? 'all' : name)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                  departmentFilter === name
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
                }`}
              >
                <FolderOpen className="h-3 w-3" />
                {name}
              </button>
            ))}
            <button
              onClick={() => setDepartmentFilter(departmentFilter === 'no-group' ? 'all' : 'no-group')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                departmentFilter === 'no-group'
                  ? 'bg-slate-600 text-white border-slate-600'
                  : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
              }`}
            >
              Sem Grupo
            </button>
          </div>
        )}
      </div>

      {/* Suppliers grouped by department */}
      {suppliers.length === 0 ? (
        <Card className="border border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
            <Truck className="h-12 w-12 sm:h-16 sm:w-16 text-slate-300 mb-4" />
            <p className="text-base sm:text-lg font-medium text-slate-900 mb-2">Sem fornecedores activos</p>
            <p className="text-sm text-muted-foreground text-center px-4">Active fornecedores nas Definições para os ver aqui</p>
          </CardContent>
        </Card>
      ) : groupedSuppliers.length === 0 ? (
        <Card className="border border-emerald-200 bg-emerald-50/40 rounded-sm shadow-none" data-testid="no-pending-suppliers">
          <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16">
            <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-emerald-500 mb-4" />
            <p className="text-base sm:text-lg font-medium text-slate-900 mb-2">Nenhum fornecedor com pendências</p>
            <p className="text-sm text-muted-foreground text-center px-4 mb-4">Todos os produtos estão cobertos ou com encomenda em curso</p>
            <Button variant="outline" size="sm" onClick={() => setShowOnlyPending(false)} data-testid="show-all-suppliers-btn">
              Mostrar todos os fornecedores
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupedSuppliers.map((group) => (
            <div key={group.name || '__no_group'}>
              {/* Group Header */}
              <div className="flex items-center gap-2 mb-4">
                {group.name ? (
                  <>
                    <div className="h-7 w-7 rounded bg-orange-100 flex items-center justify-center">
                      <Package className="h-4 w-4 text-orange-600" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-800">{group.name}</h2>
                    <Badge variant="secondary" className="text-xs">{group.suppliers.length}</Badge>
                  </>
                ) : (
                  <>
                    <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center">
                      <Truck className="h-4 w-4 text-slate-500" />
                    </div>
                    <h2 className="text-lg font-bold text-slate-500">Outros Fornecedores</h2>
                    <Badge variant="secondary" className="text-xs">{group.suppliers.length}</Badge>
                  </>
                )}
                <div className="flex-1 h-px bg-slate-200 ml-2" />
              </div>
              {/* Supplier Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {group.suppliers.map((supplier) => renderSupplierCard(supplier))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Detail Side Panel - Always render, controlled by state */}
      <Sheet open={productDetailPanel.open} onOpenChange={(open) => !open && closeProductDetailPanel()}>
        <SheetContent side="right" className="w-full sm:w-[540px] sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Detalhes do Produto
            </SheetTitle>
          </SheetHeader>
          
          {productDetailPanel.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : productDetailPanel.data ? (
            <div className="space-y-6">
              {/* Product Header */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{productDetailPanel.data.sku}</Badge>
                  {productDetailPanel.data.ai_tracking_enabled && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">PI Activo</Badge>
                  )}
                </div>
                <h3 className="text-lg font-semibold">{productDetailPanel.data.name}</h3>
                <p className="text-sm text-muted-foreground">{productDetailPanel.data.category} — {productDetailPanel.data.supplier}</p>
              </div>

              {/* Stock Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Stock Total</p>
                  <p className="text-2xl font-bold">{productDetailPanel.data.stock_real ?? productDetailPanel.data.current_stock ?? 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Reorder Point</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {productDetailPanel.data.manual_reorder_point ?? productDetailPanel.data.reorder_point ?? '—'}
                  </p>
                </div>
                {productDetailPanel.data.moq && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">MOQ</p>
                    <p className="text-lg font-semibold">{productDetailPanel.data.moq}</p>
                  </div>
                )}
                {productDetailPanel.data.lead_time_days && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Lead Time</p>
                    <p className="text-lg font-semibold">{productDetailPanel.data.lead_time_days} dias</p>
                  </div>
                )}
              </div>

              {/* Stock by Warehouse */}
              {productDetailPanel.data.stock_by_warehouse && Object.keys(productDetailPanel.data.stock_by_warehouse).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Warehouse className="h-4 w-4" />
                    Stock por Armazém
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(productDetailPanel.data.stock_by_warehouse).map(([wh, qty]) => (
                      <div key={wh} className="p-2 bg-slate-50 rounded text-center">
                        <p className="text-xs text-muted-foreground">{wh}</p>
                        <p className="font-semibold">{qty}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Info from Ficha */}
              {productDetailPanel.data.ficha && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    Preços
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {productDetailPanel.data.ficha.avg_cost != null && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600">Custo Médio (PCMP)</p>
                        <p className="font-semibold text-blue-700">{productDetailPanel.data.ficha.avg_cost.toFixed(2)}€</p>
                      </div>
                    )}
                    {productDetailPanel.data.ficha.pvp != null && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600">PVP</p>
                        <p className="font-semibold text-green-700">{productDetailPanel.data.ficha.pvp.toFixed(2)}€</p>
                      </div>
                    )}
                    {productDetailPanel.data.ficha.supplier_price != null && (
                      <div className="p-3 bg-orange-50 rounded-lg col-span-2">
                        <p className="text-xs text-orange-600">Preço Fornecedor (Última ENF)</p>
                        <p className="font-semibold text-orange-700">{productDetailPanel.data.ficha.supplier_price.toFixed(2)}€</p>
                        {productDetailPanel.data.ficha.supplier_price_doc_ref && (
                          <p className="text-xs text-orange-500 mt-1">Ref: {productDetailPanel.data.ficha.supplier_price_doc_ref}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Forecast / Demand if available */}
              {(productDetailPanel.data.avg_daily_demand || productDetailPanel.data.days_of_stock) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Previsão
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {productDetailPanel.data.avg_daily_demand && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Consumo Médio/Dia</p>
                        <p className="font-semibold">{productDetailPanel.data.avg_daily_demand.toFixed(2)}</p>
                      </div>
                    )}
                    {productDetailPanel.data.days_of_stock != null && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Dias de Stock</p>
                        <p className="font-semibold">{Math.round(productDetailPanel.data.days_of_stock)} dias</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button asChild variant="outline" className="flex-1">
                  <Link to={`/inventory/${productDetailPanel.data.id}`} target="_blank">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir Ficha Completa
                  </Link>
                </Button>
              </div>
            </div>
          ) : productDetailPanel.product ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>Não foi possível carregar os detalhes</p>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SupplierOrders;
